import React, {useState, useEffect, useRef} from "react";
import PropTypes from "prop-types";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";

//helpers:
import {getFileWithEncodedName} from "../../../../_metronic/_helpers";

//logic:
import {onTrackCreate, setTemporaryErrorMessage} from "./createTrackLogic";

//actions:
import {tracksActions} from "../../../redux/Tracks/tracksRedux";
import {settingsActions} from "../../../redux/Settings/settingsRedux";

//components:
import {makeStyles} from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import {StyledDropZone} from "react-drop-zone";
import SweetAlert from "react-bootstrap-sweetalert";
import CircularProgress from "@material-ui/core/CircularProgress";

const useStyles = makeStyles(theme => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.5)",
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: "none",
        borderRadius: "0.5rem",
        boxShadow: theme.shadows[5],
    },
}));

export const CreateTrackModal = ({open = false, tracks = [{}], setOpen = () => {}, feedback = ""}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    const dispatch = useDispatch();

    const getSettingTracks = () => dispatch(settingsActions.getSettingTracks());
    const getNormalTracks = () => dispatch(tracksActions.getNormalTracks());

    const [trackName, setTrackName] = useState("");
    const [trackNameIsNew, setTrackNameIsNew] = useState(true);
    const [trackHeaderImg, setTrackHeaderImg] = useState("");
    const [folderImg, setFolderImg] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showFailedModal, setShowFailedModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const nameInputRef = useRef(null);

    const IMAGES_TYPES = {
        header: "header",
        folder: "folder",
    };

    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setTrackName("");
                setTrackHeaderImg("");
                setFolderImg("");
            }, 800);
        }
    }, [open]);

    useEffect(() => {
        if (!showFailedModal) setErrorMessage("");
    }, [showFailedModal]);

    const checkNewNameExists = name => {
        const alreadyExists = tracks?.find(track => track.name.toLowerCase() === name.toLowerCase());

        if (alreadyExists) {
            setTrackNameIsNew(false);
            nameInputRef?.current && nameInputRef.current.focus();

            const message = t("MODAL.CREATE_TRACK.NAME_EXISTING");
            setTemporaryErrorMessage(setErrorMessage, message);
        } else {
            setTrackNameIsNew(true);
        }
    };

    const handleDropImage = (type, file) => {
        if (type && file) {
            const MAX_SIZE_ALLOWED = 5;
            const size = file.size / 1024 / 1024; // in MB
            if (size < MAX_SIZE_ALLOWED) {
                if (type === IMAGES_TYPES.header) {
                    setTrackHeaderImg({
                        preview: URL.createObjectURL(file),
                        raw: file,
                    });
                } else if (type === IMAGES_TYPES.folder) {
                    setFolderImg({
                        preview: URL.createObjectURL(file),
                        raw: file,
                    });
                }
            } else {
                const message = t("MODAL.CREATE_TRACK.EXCEEDS_SIZE", {
                    size: MAX_SIZE_ALLOWED,
                });
                setTemporaryErrorMessage(setErrorMessage, message);
            }
        }
    };

    const handleUpload = () => {
        const headerImgWithEncodedName = getFileWithEncodedName(trackHeaderImg.raw);
        const folderImgWithEncodedName = getFileWithEncodedName(folderImg.raw);

        onTrackCreate({
            t,
            name: trackName,
            headerImg: headerImgWithEncodedName,
            folderImg: folderImgWithEncodedName,
            actions: {
                setErrorMessage: msg => setErrorMessage(msg),
                updateTracks: () => {
                    getSettingTracks();
                    getNormalTracks();
                },
                showSuccessModal: () => setShowSuccessModal(true),
                hideLoader: () => setIsLoading(false),
                showFailModal: () => setShowFailedModal(true),
                closeCreateModal: () => setOpen(false),
            },
        });
        setIsLoading(true);
    };

    return (
        <>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                className={`${classes.modal} uploadmodal`}
                open={open}
                onClose={() => setOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <div className={`create_track_modal  ${classes.paper}`}>
                        {isLoading ? (
                            <div
                                className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100"
                                style={{
                                    opacity: isLoading ? 1 : 0,
                                    zIndex: isLoading ? 1 : -1,
                                    transition: "0.4s ease all",
                                }}
                            >
                                <CircularProgress />
                            </div>
                        ) : null}
                        <>
                            <div className="py-10 py-lg-12 text-center">
                                <h3 className="text-muted font-weight-bold">{t("MODAL.CREATE_TRACK.TITLE")}</h3>
                            </div>
                            <input
                                type="text"
                                variant="outlined"
                                ref={nameInputRef}
                                autoFocus
                                className="mb-6 rounded"
                                value={trackName}
                                onChange={e => setTrackName(e.target.value.replace(/\s\s+/g, " "))}
                                onBlur={e => {
                                    const value = e.target.value?.trim();
                                    if (value) checkNewNameExists(value);
                                    setTrackName(value);
                                }}
                                placeholder={t("MODAL.CREATE_TRACK.WRITE_NAME")}
                            />
                            <div className="row">
                                <div className="col-12 col-md-6 gutter-b">
                                    <StyledDropZone
                                        accept={"image/*"}
                                        className="h-100"
                                        onDrop={file => {
                                            handleDropImage(IMAGES_TYPES.folder, file);
                                        }}
                                    >
                                        <div className="h-100 w-100 d-flex justify-content-center align-items-center">
                                            {folderImg?.preview ? (
                                                <img
                                                    className="w-50px h-50px position-absolute"
                                                    src={folderImg.preview}
                                                    alt="new video img"
                                                />
                                            ) : (
                                                <>
                                                    {trackName
                                                        ? t("MODAL.CREATE_TRACK.DROP_THUMBNAIL_TRACKNAME", {
                                                              trackName,
                                                          })
                                                        : t("MODAL.CREATE_TRACK.DROP_THUMBNAIL")}
                                                </>
                                            )}
                                        </div>
                                    </StyledDropZone>
                                </div>
                                <div className="col-12 col-md-6 gutter-b">
                                    <StyledDropZone
                                        accept={"image/*"}
                                        className="h-100"
                                        onDrop={file => {
                                            handleDropImage(IMAGES_TYPES.header, file);
                                        }}
                                    >
                                        <div className="h-100 w-100 d-flex justify-content-center align-items-center">
                                            {trackHeaderImg?.preview ? (
                                                <img
                                                    className="w-50px h-50px position-absolute"
                                                    src={trackHeaderImg.preview}
                                                    alt="new video header img"
                                                />
                                            ) : (
                                                <>
                                                    {trackName
                                                        ? t("MODAL.CREATE_TRACK.DROP_HEADER_TRACKNAME", {
                                                              trackName,
                                                          })
                                                        : t("MODAL.CREATE_TRACK.DROP_HEADER")}
                                                </>
                                            )}
                                        </div>
                                    </StyledDropZone>
                                </div>
                            </div>
                            <p className="my-4 text-danger">{errorMessage || feedback}</p>
                            <div className="d-flex justify-content-between w-100">
                                <button
                                    className="btn btn-light font-weight-bolder py-4 px-7 my-3"
                                    onClick={() => setOpen(false)}
                                >
                                    {t("GENERAL.CANCEL")}
                                </button>
                                <button
                                    className="btn btn-primary font-weight-bolder py-4 px-7 my-3 ml-auto"
                                    style={{
                                        cursor:
                                            !trackName || !trackHeaderImg || !folderImg || !trackNameIsNew
                                                ? "auto"
                                                : "pointer",
                                    }}
                                    disabled={!trackName || !trackHeaderImg || !folderImg || !trackNameIsNew}
                                    onClick={handleUpload}
                                >
                                    {t("GENERAL.CONFIRM")}
                                </button>
                            </div>
                        </>
                    </div>
                </Fade>
            </Modal>
            <SweetAlert
                show={showSuccessModal}
                success
                title={t("MODAL.CREATE_TRACK.SUCCESS")}
                confirmBtnText={t("GENERAL.OK")}
                confirmBtnBsStyle="primary"
                onConfirm={() => {
                    setShowSuccessModal(false);
                }}
            />
            <SweetAlert
                show={showFailedModal}
                error
                title={t("MODAL.CREATE_TRACK.FAIL")}
                confirmBtnText={t("GENERAL.OK")}
                confirmBtnBsStyle="primary"
                onConfirm={() => {
                    setShowFailedModal(false);
                }}
            >
                {errorMessage}
            </SweetAlert>
        </>
    );
};

CreateTrackModal.propTypes = {
    onConfirm: PropTypes.func,
    open: PropTypes.bool,
    tracks: PropTypes.arrayOf(PropTypes.object),
    setOpen: PropTypes.func,
};
